(function () {
  'use strict';

  const DEFAULT_STATIC_SLIDE_DURATION = 6000;
  const DEFAULT_TRANSITION_DURATION = 1000;
  const FEATURE_FADE_DURATION = 420;
  const PREWARM_ROOT_MARGIN = 800;

  function prewarmMedia(container) {
    if (!container) return;

    container.querySelectorAll('img').forEach((image) => {
      image.loading = 'eager';
      image.decoding = 'async';
      if (typeof image.decode === 'function' && !image.complete) {
        image.decode().catch(() => {});
      }
    });

    container.querySelectorAll('video').forEach((video) => {
      video.preload = 'auto';
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      if (video.readyState === 0) video.load();

      const shouldWarmPlay = video.autoplay || video.hasAttribute('autoplay') || video.dataset.hpgVideoIdleAutoplay === 'true';
      const canAutoplay = video.muted || video.defaultMuted || video.hasAttribute('muted');
      if (shouldWarmPlay && canAutoplay && video.paused) {
        const playResult = video.play();
        if (playResult && typeof playResult.catch === 'function') {
          playResult.catch(() => {});
        }
      }
    });
  }

  class HpgSystemCarousel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.viewport = this.querySelector('[data-hpg-carousel-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-hpg-carousel-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-hpg-carousel-dot]'));
      this.playButton = this.querySelector('[data-hpg-carousel-play]');
      this.featureButtons = Array.from(this.querySelectorAll('[data-hpg-system-feature]'));
      this.staticSlideDuration = this.readDuration(this.dataset.staticSlideDuration, DEFAULT_STATIC_SLIDE_DURATION);
      this.transitionDuration = this.readDuration(this.dataset.slideTransitionDuration, DEFAULT_TRANSITION_DURATION);
      this.isPlaying = false;
      this.userPaused = false;
      this.hasSequenceStarted = false;
      this.playbackVersion = 0;
      this.navigationVersion = 0;
      this.slideTimerId = null;
      this.slideTimerRemaining = this.staticSlideDuration;
      this.slideTimerStartedAt = 0;
      this.slideTimerSlideIndex = null;
      this.scrollFrameId = null;
      this.activeVideo = null;
      this.activeVideoEnded = null;
      this.activeVideoProgress = null;
      this.activeVideoMetadata = null;
      this.activeVideoProgressFrameId = null;
      this.prewarmObserver = null;
      this.pendingPrewarmIndex = 0;
      this.viewportVisibilityFrameId = null;
      this.boundViewportVisibilityChange = this.onViewportVisibilityChange.bind(this);
      this.boundVideoPlaying = this.onVideoPlaying.bind(this);
      this.boundVideoPause = this.onVideoPause.bind(this);

      if (!this.viewport || !this.slides.length) return;

      this.viewport.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      window.addEventListener('scroll', this.boundViewportVisibilityChange, { passive: true });
      window.addEventListener('resize', this.boundViewportVisibilityChange);
      this.querySelectorAll('.hpg-system-intro__video-media--has-poster video').forEach((video) => {
        video.addEventListener('playing', this.boundVideoPlaying);
        video.addEventListener('pause', this.boundVideoPause);
        video.addEventListener('ended', this.boundVideoPause);
      });
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.navigateToSlide(Number(dot.dataset.hpgCarouselDot || 0));
        });
      });

      this.slides.forEach((slide, index) => {
        slide.addEventListener('click', (event) => this.onSlideClick(event, index));
      });

      if (this.playButton) {
        this.playButton.addEventListener('click', () => this.togglePlay());
      }

      this.featureButtons.forEach((button) => {
        button.addEventListener('click', () => this.setFeature(button));
      });

      this.setActive(0);
      this.prewarmNearby(0);
      this.onViewportVisibilityChange();
    }

    disconnectedCallback() {
      this.stop();
      this.clearScrollAnimation();
      window.clearTimeout(this.scrollTimeout);
      if (this.viewportVisibilityFrameId) {
        window.cancelAnimationFrame(this.viewportVisibilityFrameId);
        this.viewportVisibilityFrameId = null;
      }
      window.removeEventListener('scroll', this.boundViewportVisibilityChange);
      window.removeEventListener('resize', this.boundViewportVisibilityChange);
      this.querySelectorAll('.hpg-system-intro__video-media--has-poster video').forEach((video) => {
        video.removeEventListener('playing', this.boundVideoPlaying);
        video.removeEventListener('pause', this.boundVideoPause);
        video.removeEventListener('ended', this.boundVideoPause);
      });
      this.disconnectPrewarmObserver();
    }

    readDuration(value, fallback) {
      const duration = Number(value);
      return Number.isFinite(duration) && duration > 0 ? duration : fallback;
    }

    getActiveIndex() {
      const viewportLeft = this.viewport.getBoundingClientRect().left;
      const scrollPaddingLeft = this.getScrollPaddingLeft();
      let activeIndex = 0;
      let activeDistance = Number.POSITIVE_INFINITY;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.getBoundingClientRect().left - viewportLeft - scrollPaddingLeft);
        if (distance < activeDistance) {
          activeDistance = distance;
          activeIndex = index;
        }
      });

      return activeIndex;
    }

    getScrollPaddingLeft() {
      const value = window.getComputedStyle(this.viewport).scrollPaddingLeft;
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    onScroll() {
      window.clearTimeout(this.scrollTimeout);
      this.scrollTimeout = window.setTimeout(() => {
        const nextIndex = this.getActiveIndex();
        if (nextIndex === this.activeIndex) return;

        const shouldContinue = this.isPlaying;
        this.playbackVersion += 1;
        this.clearSlidePlayback();
        this.pauseAllSlideVideos();
        this.resetPlaybackProgress();
        this.setActive(nextIndex);

        if (shouldContinue) {
          this.hasSequenceStarted = true;
          this.startActiveSlide({ restart: true });
        } else {
          this.hasSequenceStarted = false;
        }
      }, 120);
    }

    async goTo(index) {
      const nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      const nextSlide = this.slides[nextIndex];
      if (!nextSlide) return;

      this.setActive(nextIndex);
      await this.scrollToSlide(nextSlide);
    }

    scrollToSlide(slide) {
      const viewportRect = this.viewport.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const target = this.viewport.scrollLeft + slideRect.left - viewportRect.left - this.getScrollPaddingLeft();
      return this.animateScrollTo(Math.max(0, target));
    }

    animateScrollTo(target) {
      this.clearScrollAnimation();

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.viewport.scrollLeft = target;
        return Promise.resolve();
      }

      const start = this.viewport.scrollLeft;
      const distance = target - start;
      const duration = this.transitionDuration;
      const startedAt = window.performance.now();

      this.viewport.classList.add('is-animating');

      return new Promise((resolve) => {
        const step = (now) => {
          const elapsed = Math.min((now - startedAt) / duration, 1);
          const eased = elapsed < 0.5
            ? 4 * elapsed * elapsed * elapsed
            : 1 - Math.pow(-2 * elapsed + 2, 3) / 2;

          this.viewport.scrollLeft = start + distance * eased;

          if (elapsed < 1) {
            this.scrollFrameId = window.requestAnimationFrame(step);
          } else {
            this.scrollFrameId = null;
            this.viewport.classList.remove('is-animating');
            resolve();
          }
        };

        this.scrollFrameId = window.requestAnimationFrame(step);
      });
    }

    clearScrollAnimation() {
      if (this.scrollFrameId) {
        window.cancelAnimationFrame(this.scrollFrameId);
        this.scrollFrameId = null;
      }
      this.viewport?.classList.remove('is-animating');
    }

    onSlideClick(event, index) {
      if (index === this.activeIndex) return;
      if (event.target.closest('a, button, input, select, textarea, video')) return;
      this.navigateToSlide(index);
    }

    async navigateToSlide(index) {
      const shouldContinue = this.isPlaying || (!this.userPaused && this.hasSequenceStarted);
      const navigationVersion = ++this.navigationVersion;
      this.playbackVersion += 1;

      this.clearSlidePlayback();
      this.pauseAllSlideVideos();
      this.resetPlaybackProgress();

      await this.goTo(index);

      if (
        navigationVersion !== this.navigationVersion
        || !shouldContinue
        || this.userPaused
        || !this.hasSequenceStarted
        || !this.isActiveSlideFullyInViewport()
      ) return;

      this.isPlaying = true;
      this.hasSequenceStarted = true;
      this.setPlayButtonState(true, 'Pause carousel');
      this.startActiveSlide({ restart: true });
    }

    setActive(index) {
      this.activeIndex = index;
      this.slides.forEach((slide, slideIndex) => {
        slide.toggleAttribute('aria-current', slideIndex === index);
      });
      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        const isVideoProgress = active && Boolean(this.getSlideVideo(this.slides[index]));
        dot.classList.toggle('is-active', active);
        dot.classList.toggle('is-video-progress', isVideoProgress);
        dot.setAttribute('aria-current', active ? 'true' : 'false');

        if (!active) {
          dot.style.removeProperty('--hpg-carousel-dot-progress');
        } else if (isVideoProgress && !dot.style.getPropertyValue('--hpg-carousel-dot-progress')) {
          dot.style.setProperty('--hpg-carousel-dot-progress', '0');
        }
      });

      this.prewarmNearby(index);
    }

    onViewportVisibilityChange() {
      if (this.viewportVisibilityFrameId) return;

      this.viewportVisibilityFrameId = window.requestAnimationFrame(() => {
        this.viewportVisibilityFrameId = null;
        const isInView = this.isActiveSlideFullyInViewport();

        if (isInView) {
          if (!this.userPaused && !this.isPlaying) {
            this.play({
              automatic: true,
              restart: !this.hasSequenceStarted,
            });
          }
          return;
        }

        if (this.isPlaying) {
          this.pause();
        }
      });
    }

    onVideoPlaying(event) {
      event.currentTarget.closest('.hpg-system-intro__video-media--has-poster')?.classList.add('is-video-playing');
    }

    onVideoPause(event) {
      event.currentTarget.closest('.hpg-system-intro__video-media--has-poster')?.classList.remove('is-video-playing');
    }

    isActiveSlideFullyInViewport() {
      const slide = this.slides[this.activeIndex];
      if (!slide) return false;

      const rect = slide.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const visibleWidth = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0));
      const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
      const visibleArea = visibleWidth * visibleHeight;
      const totalArea = Math.max(rect.width * rect.height, 1);
      const visibleRatio = visibleArea / totalArea;

      if (window.matchMedia('(max-width: 749px)').matches) {
        return visibleRatio >= 0.12 && visibleHeight / Math.max(rect.height, 1) >= 0.12;
      }

      return visibleRatio >= 0.45
        && visibleWidth / Math.max(rect.width, 1) >= 0.75
        && visibleHeight / Math.max(rect.height, 1) >= 0.4;
    }

    prewarmNearby(index) {
      if (!this.isNearPrewarmRange() && 'IntersectionObserver' in window) {
        this.observePrewarm(index);
        return;
      }

      this.disconnectPrewarmObserver();

      [index, index + 1].forEach((slideIndex) => {
        const slide = this.slides[slideIndex];
        prewarmMedia(slide);

        if (slide?.classList.contains('hpg-system-intro__slide--system')) {
          slide.querySelectorAll('[data-hpg-feature-panel]').forEach(prewarmMedia);
        }
      });
    }

    isNearPrewarmRange() {
      const rect = this.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      return rect.top < viewportHeight + PREWARM_ROOT_MARGIN && rect.bottom > -PREWARM_ROOT_MARGIN;
    }

    observePrewarm(index) {
      this.pendingPrewarmIndex = index;
      if (this.prewarmObserver) return;

      this.prewarmObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        const prewarmIndex = this.pendingPrewarmIndex;
        this.disconnectPrewarmObserver();
        this.prewarmNearby(prewarmIndex);
      }, {
        rootMargin: `${PREWARM_ROOT_MARGIN}px 0px`,
        threshold: 0,
      });

      this.prewarmObserver.observe(this);
    }

    disconnectPrewarmObserver() {
      if (!this.prewarmObserver) return;

      this.prewarmObserver.disconnect();
      this.prewarmObserver = null;
    }

    setFeature(button) {
      const slide = button.closest('[data-hpg-carousel-slide]');
      if (!slide) return;

      const featureIndex = button.dataset.hpgSystemFeature;
      const buttons = Array.from(slide.querySelectorAll('[data-hpg-system-feature]'));
      const panels = Array.from(slide.querySelectorAll('[data-hpg-feature-panel]'));
      const copyPanels = Array.from(slide.querySelectorAll('[data-hpg-feature-copy]'));

      buttons.forEach((featureButton) => {
        const active = featureButton === button;
        featureButton.classList.toggle('is-active', active);
        featureButton.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      panels.forEach((panel) => {
        const active = panel.dataset.hpgFeaturePanel === featureIndex;
        this.setFeaturePanelVisibility(panel, active);
        if (active) prewarmMedia(panel);
      });

      copyPanels.forEach((panel) => {
        const active = panel.dataset.hpgFeatureCopy === featureIndex;
        this.setFeaturePanelVisibility(panel, active);
      });

      if (this.isPlaying && slide === this.slides[this.activeIndex] && !this.getSlideVideo(slide)) {
        this.scheduleStaticSlide({ restart: true });
      }
    }

    setFeaturePanelVisibility(panel, active) {
      window.clearTimeout(panel.hpgFeatureFadeTimer);

      if (active) {
        panel.hidden = false;
        panel.offsetHeight;
        panel.classList.add('is-active');
        return;
      }

      panel.classList.remove('is-active');
      panel.hpgFeatureFadeTimer = window.setTimeout(() => {
        if (!panel.classList.contains('is-active')) panel.hidden = true;
      }, FEATURE_FADE_DURATION);
    }

    togglePlay() {
      if (this.isPlaying) {
        this.pause({ userInitiated: true });
      } else {
        this.userPaused = false;
        this.play();
      }
    }

    play(options = {}) {
      const automatic = Boolean(options.automatic);
      const restart = options.restart ?? !this.hasSequenceStarted;
      if (automatic && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.isPlaying = true;
      this.hasSequenceStarted = true;
      this.setPlayButtonState(true, 'Pause carousel');
      this.startActiveSlide({ restart });
    }

    pause(options = {}) {
      if (options.userInitiated) {
        this.userPaused = true;
      }

      if (this.activeVideo) {
        this.activeVideo.pause();
      }

      this.playbackVersion += 1;
      this.isPlaying = false;
      this.setPlayButtonState(false, 'Play carousel');
      this.clearSlidePlayback({ preserveTimer: true });
    }

    stop() {
      this.playbackVersion += 1;
      this.isPlaying = false;
      this.userPaused = false;
      this.hasSequenceStarted = false;
      this.setPlayButtonState(false, 'Play carousel');
      this.clearSlidePlayback();
      this.pauseAllSlideVideos();
      this.resetPlaybackProgress();
    }

    setPlayButtonState(isActive, label) {
      this.playButton?.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      this.playButton?.setAttribute('aria-label', label);
      this.playButton?.classList.toggle('is-playing', isActive);
    }

    startActiveSlide(options = {}) {
      const restart = Boolean(options.restart);
      this.clearSlidePlayback();
      if (!this.isPlaying) return;

      const slide = this.slides[this.activeIndex];
      const video = this.getSlideVideo(slide);

      this.pauseInactiveSlideVideos(slide);

      if (video) {
        this.playVideoSlide(video, { restart });
      } else {
        this.scheduleStaticSlide({ restart });
      }
    }

    getSlideVideo(slide) {
      const videos = this.getSlideVideos(slide);
      return videos.find((video) => this.isElementVisible(video)) || null;
    }

    getSlideVideos(slide) {
      return Array.from(slide?.querySelectorAll('.hpg-system-intro__slide-media video, .hpg-system-intro__single-media video') || []);
    }

    isElementVisible(element) {
      if (!element) return false;

      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;

      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    }

    pauseSlideVideos(slide, exceptVideo = null) {
      this.getSlideVideos(slide).forEach((video) => {
        if (video === exceptVideo) return;
        video.pause();
      });
    }

    playVideoSlide(video, options = {}) {
      const restart = Boolean(options.restart);
      this.pauseSlideVideos(video.closest('[data-hpg-carousel-slide]'), video);
      this.activeVideo = video;
      this.activeVideo.loop = false;
      this.activeVideo.muted = true;
      this.activeVideo.playsInline = true;
      this.activeVideoEnded = () => {
        this.updateVideoProgress(video);
        this.advanceFromActive();
      };
      this.activeVideoProgress = () => this.updateVideoProgress(video);
      this.activeVideoMetadata = () => this.updateVideoProgress(video);
      this.activeVideo.addEventListener('ended', this.activeVideoEnded, { once: true });
      this.activeVideo.addEventListener('timeupdate', this.activeVideoProgress);
      this.activeVideo.addEventListener('loadedmetadata', this.activeVideoMetadata);
      this.activeVideo.addEventListener('durationchange', this.activeVideoMetadata);

      if (restart) {
        try {
          this.activeVideo.currentTime = 0;
        } catch (error) {
          // Some browser/video states reject seeking before metadata loads.
        }
      }

      this.updateVideoProgress(video);
      this.startVideoProgressLoop(video);

      const playResult = this.activeVideo.play();
      if (playResult && typeof playResult.catch === 'function') {
        playResult.catch(() => {
          if (this.isPlaying && this.activeVideo === video) {
            this.scheduleStaticSlide({ restart: true });
          }
        });
      }
    }

    startVideoProgressLoop(video) {
      if (this.activeVideoProgressFrameId) {
        window.cancelAnimationFrame(this.activeVideoProgressFrameId);
      }

      const update = () => {
        if (this.activeVideo !== video) return;

        this.updateVideoProgress(video);
        this.activeVideoProgressFrameId = window.requestAnimationFrame(update);
      };

      this.activeVideoProgressFrameId = window.requestAnimationFrame(update);
    }

    updateVideoProgress(video) {
      if (this.activeVideo !== video) return;

      const dot = this.dots[this.activeIndex];
      if (!dot) return;

      const duration = Number(video.duration);
      const currentTime = Number(video.currentTime);
      const progress = Number.isFinite(duration) && duration > 0 && Number.isFinite(currentTime)
        ? Math.max(0, Math.min(currentTime / duration, 1))
        : 0;

      dot.style.setProperty('--hpg-carousel-dot-progress', progress.toFixed(4));
    }

    scheduleStaticSlide(options = {}) {
      const restart = Boolean(options.restart);
      this.clearSlideTimer();
      if (!this.isPlaying) return;

      if (
        restart
        || this.slideTimerSlideIndex !== this.activeIndex
        || this.slideTimerRemaining <= 0
      ) {
        this.slideTimerRemaining = this.staticSlideDuration;
      }

      this.slideTimerSlideIndex = this.activeIndex;
      this.slideTimerStartedAt = window.performance.now();
      this.slideTimerId = window.setTimeout(() => {
        this.slideTimerId = null;
        this.slideTimerStartedAt = 0;
        this.slideTimerRemaining = 0;
        this.advanceFromActive();
      }, this.slideTimerRemaining);
    }

    async advanceFromActive() {
      if (!this.isPlaying) return;

      const playbackVersion = this.playbackVersion;
      const nextIndex = (this.activeIndex + 1) % this.slides.length;
      this.resetPlaybackProgress();

      await this.goTo(nextIndex);

      if (this.isPlaying && playbackVersion === this.playbackVersion) {
        this.startActiveSlide({ restart: true });
      }
    }

    clearSlidePlayback(options = {}) {
      if (options.preserveTimer) {
        this.pauseSlideTimer();
      } else {
        this.clearSlideTimer();
      }

      this.clearActiveVideoTracking();
    }

    clearSlideTimer() {
      if (this.slideTimerId) {
        window.clearTimeout(this.slideTimerId);
        this.slideTimerId = null;
      }

      this.slideTimerStartedAt = 0;
    }

    pauseSlideTimer() {
      if (this.slideTimerId && this.slideTimerStartedAt) {
        const elapsed = window.performance.now() - this.slideTimerStartedAt;
        this.slideTimerRemaining = Math.max(0, this.slideTimerRemaining - elapsed);
      }

      this.clearSlideTimer();
    }

    resetPlaybackProgress() {
      this.slideTimerRemaining = this.staticSlideDuration;
      this.slideTimerStartedAt = 0;
      this.slideTimerSlideIndex = null;
    }

    clearActiveVideoTracking() {
      if (this.activeVideo && this.activeVideoEnded) {
        this.activeVideo.removeEventListener('ended', this.activeVideoEnded);
      }

      if (this.activeVideo && this.activeVideoProgress) {
        this.activeVideo.removeEventListener('timeupdate', this.activeVideoProgress);
      }

      if (this.activeVideo && this.activeVideoMetadata) {
        this.activeVideo.removeEventListener('loadedmetadata', this.activeVideoMetadata);
        this.activeVideo.removeEventListener('durationchange', this.activeVideoMetadata);
      }

      if (this.activeVideoProgressFrameId) {
        window.cancelAnimationFrame(this.activeVideoProgressFrameId);
        this.activeVideoProgressFrameId = null;
      }

      this.activeVideo = null;
      this.activeVideoEnded = null;
      this.activeVideoProgress = null;
      this.activeVideoMetadata = null;
    }

    pauseInactiveSlideVideos(activeSlide) {
      this.slides.forEach((slide) => {
        if (slide === activeSlide) return;
        this.pauseSlideVideos(slide);
      });
    }

    pauseAllSlideVideos() {
      this.slides.forEach((slide) => {
        this.pauseSlideVideos(slide);
      });
    }
  }

  if (!customElements.get('hpg-system-carousel')) {
    customElements.define('hpg-system-carousel', HpgSystemCarousel);
  }
})();
